// Sandboxed WEBSITE preview builder — Website Studio runtime
// (creative-code-studio-website-prd; the backend agent prompt in
// platform-backend/src/code-sessions/website-prompt.ts teaches EXACTLY this
// contract — keep the two in sync).
//
// Unlike the game builder (a studio-owned shell hosting kid game.js), the VFS
// owns REAL pages here: the selected `.html` file provides the CONTENT — but
// the DOCUMENT SKELETON is studio-owned. Per build:
//
//   1. PAGE SELECTION — `opts.page` (default index.html) picks the page; a
//      missing page falls back to index.html; no index.html at all renders a
//      friendly kid-facing empty state.
//   2. STUDIO-OWNED SKELETON (security-load-bearing) — the srcdoc is ALWAYS
//      `<!doctype html><html><head>` + the shims + CSP + server.js, and only
//      THEN the kid page's lifted head/body. The kid page is parsed with
//      DOMParser (never executes, repairs malformed markup) and its head
//      children + body are lifted INTO the skeleton — the injection point is
//      never located by matching markup in untrusted HTML, so a `<head>`
//      hidden in a comment / string literal / attribute cannot displace the
//      shims (that bypass would disarm the fetch shim AND the CSP, the two
//      fences the backend's fetch( allowance for websites rests on, D-WEB-03).
//   3. SHIM ORDER inside the skeleton head: extension-noise guard, console
//      capture, the ASSET-ORIGIN shim (`../code/assetOrigin.ts`: a project path
//      first seen at RUN time — a db row, an /api/ response — resolves to the
//      inlined bytes; `assets.url()`), the SITE RUNTIME shim (the `db.query` sql postMessage channel
//      to the project's REAL server-side SQLite db + `app.get/app.post` + the
//      /api-only fetch shim + the page-link nav shim), the deny-by-default CSP
//      meta, then server.js — ALWAYS the first kid script, whether or not the
//      page references it (it defines the routes).
//   4. REFERENCE INLINING — `<link rel="stylesheet" href>` / `<script src>`
//      tags are replaced with the referenced VFS file inlined (scripts carry
//      `//# sourceURL=<path>`; a literal `</script>`/`</style>` in kid text is
//      escaped so it can't truncate the tag); a missing/external reference
//      becomes a console.error (never a crash). Project asset references
//      (quoted paths, unquoted CSS `url()`, `./` / `/` / `%20` / case
//      variants) are rewritten to data: URLs via the game builder's
//      `inlineAssetRefs`; a media reference that matches NO project file is
//      named in a console.error at build time (an unmatched relative path
//      would otherwise resolve against the studio origin, be blocked by the
//      CSP, and read like an "internet picture" to the kid).
//   5. `scriptRanges` map srcdoc lines back to kid files so SYNTAX errors
//      (where sourceURL never applies) still resolve via `resolveErrorLoc` —
//      the same contract as `buildGamePreview`.
//
// Security model is IDENTICAL to the game: an opaque-origin iframe,
// `sandbox="allow-scripts"` only, postMessage the only channel out — plus the
// deny-by-default CSP below as the second fence.

import { MOTION_VENDOR_URL } from 'virtual:engine-vendors';

import type { VfsFile } from '../code/codeApi';
import { ASSET_ORIGIN_GLOBAL, buildAssetOriginShim, missingMediaMessage } from '../code/assetOrigin';
import { CONSOLE_CAPTURE, EXTENSION_NOISE_GUARD, SANDBOX_STORAGE_SHIM } from '../code/buildPreview';
import { findAssetByRef, inlineAssetRefs, normalizeAssetRef, type ScriptLineRange } from './buildGamePreview';

// The build-time AND run-time wording for a media reference that matches no
// project file is ONE function (asset-origin shim) — re-exported for callers.
export { missingMediaMessage };
import { CHATBOT_TRANSCRIPT_CAP, type ChatTranscriptItem } from './chatbotTranscript';

// Re-export the shared console/error-mapping contract so site components import
// from one place (mirrors buildGamePreview's re-exports).
export { isConsoleMessage, type ConsoleLine } from '../code/buildPreview';
export { resolveErrorLoc } from './buildGamePreview';
export type { ScriptLineRange } from './buildGamePreview';

/** The site's home page — the default document and every fallback target. */
export const SITE_HOME_PAGE = 'index.html';
/** Versioned contract generated websites use instead of reaching into Motion directly. */
export const WEBSITE_RUNTIME_VERSION = '1';
/** The site's backend file — always injected FIRST among kid scripts. */
const SERVER_PATH = 'server.js';

/**
 * Trusted, studio-owned website toolkit. The vendor tag is emitted before the
 * meta CSP (meta policies apply only after they are encountered), while child
 * markup remains after the CSP and cannot load another script. Generated sites
 * use the small AirbotixWeb facade so Motion can be upgraded behind a versioned
 * contract rather than becoming project source.
 */
const WEBSITE_RUNTIME_KIT = [
  `<script src="${MOTION_VENDOR_URL}" data-airbotix-runtime="motion"></script>`,
  `<script data-airbotix-runtime="website-${WEBSITE_RUNTIME_VERSION}">
(function () {
  var motion = window.Motion;
  var finished = Promise.resolve();
  var noAnimation = function () {
    return Object.freeze({
      cancel: function () {},
      complete: function () {},
      pause: function () {},
      play: function () {},
      stop: function () {},
      finished: finished,
      then: finished.then.bind(finished)
    });
  };
  var fallbackMotion = Object.freeze({
    animate: noAnimation,
    inView: function () { return function () {}; },
    scroll: function () { return function () {}; },
    stagger: function () { return 0; },
    spring: function () { return 'ease-out'; }
  });
  window.AirbotixWeb = Object.freeze({
    runtimeVersion: ${JSON.stringify(WEBSITE_RUNTIME_VERSION)},
    reducedMotion: window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    motion: Object.freeze({
      animate: motion ? motion.animate : fallbackMotion.animate,
      inView: motion ? motion.inView : fallbackMotion.inView,
      scroll: motion ? motion.scroll : fallbackMotion.scroll,
      stagger: motion ? motion.stagger : fallbackMotion.stagger,
      spring: motion ? motion.spring : fallbackMotion.spring
    })
  });
  if (!motion) {
    console.warn('The Website Studio motion toolkit is unavailable; showing the site without animation.');
  }
})();
</script>`,
].join('\n');

/** Neutral browser foundation: accessibility and responsive safety, no theme. */
const WEBSITE_FOUNDATION = `<style data-airbotix-runtime="foundation-${WEBSITE_RUNTIME_VERSION}">
*, *::before, *::after { box-sizing: border-box; }
html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
body { margin: 0; min-width: 0; }
img, picture, video, canvas, svg { display: block; max-width: 100%; }
button, input, textarea, select { font: inherit; }
button, a { touch-action: manipulation; }
:focus-visible { outline: 3px solid currentColor; outline-offset: 3px; }
[hidden] { display: none !important; }
@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto !important; }
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>`;

/**
 * Deny-by-default CSP second fence. Everything the runtime legitimately uses is
 * inline or `data:`/`blob:` (shim + kid scripts are inline; styles are inline;
 * assets are inlined data: URLs), so every SUBRESOURCE-load vector is blocked
 * wholesale even where the reference-inlining regex wouldn't catch it:
 * external `<script src>` / `<img>` / `<link>` / font / `<iframe>` (a nested
 * frame is the classic way to get a fresh un-shimmed `window.fetch`), plus
 * XHR / WebSocket / EventSource / sendBeacon via `connect-src 'none'` (which
 * also leaves the fetch shim unaffected — it never performs real network I/O).
 *
 * ⚠️ KNOWN RESIDUAL — this does NOT stop the frame navigating ITSELF to an
 * external URL (`location.href = …`, `<meta http-equiv="refresh">`, a
 * programmatic link click): CSP has no directive for document self-navigation
 * (`navigate-to` was never shipped) and `sandbox="allow-scripts"` explicitly
 * permits a frame to navigate itself. A determined kid/AI script can therefore
 * still leak page-derived data as a GET in that URL. Accepted for now: it
 * destroys the running preview (the kid sees it), it is one-shot, and closing
 * it needs `allow-top-navigation`-style sandbox work or a navigation-blocking
 * shim — tracked as a follow-up, NOT claimed as closed here.
 *
 * `img-src https:` — OWNER DECISION 2026-09-22 ("we should be able to use any
 * links for images even not on our site"): a website may show a picture from
 * ANY https:// link (a Library url, a link the kid pasted). Accepted residuals,
 * recorded in creative-code-studio-website-prd D-WEB-25: an image GET is a
 * small one-way channel out of the sandbox (same class as the self-navigation
 * residual above) and the picture content is uncurated. http:// links stay
 * blocked (mixed content on the https studio anyway). Sounds, fonts, scripts,
 * styles and frames remain project-only.
 */
const CSP_META =
  `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; ` +
  `script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob: https:; ` +
  // `connect-src data:` — the asset-origin shim answers fetch()/XHR of a project
  // file with its inlined data: URL (no network is involved); every real
  // connection (http/https/ws) stays blocked exactly as with 'none'.
  `media-src data: blob:; font-src data:; connect-src data:">`;

/** Embed a string safely inside injected `<script>` source (a literal
 *  `</script>` or `<!--` in kid data must never terminate/confuse the tag). */
const jsStr = (s: string): string => JSON.stringify(s).replace(/</g, '\\u003c');

/** Escape a literal `</script>` inside inlined kid JS so it can't terminate the
 *  studio's `<script>` tag (it legally appears only in strings/comments, where
 *  `<\/script` is byte-equivalent). Never changes line counts. */
const escapeScriptText = (s: string): string => s.replace(/<\/script/gi, '<\\/script');
/** Same fence for a literal `</style>` inside inlined kid CSS. */
const escapeStyleText = (s: string): string => s.replace(/<\/style/gi, '<\\/style');

/** Strip a leading `./` from a relative reference (`./about.html` → `about.html`). */
const normalizeRef = (ref: string): string => (ref.startsWith('./') ? ref.slice(2) : ref);

/** How long the in-frame `db.query` waits for the studio's sql reply before
 *  rejecting kid-readably (a wedged studio must never hang a kid's route). */
export const SITE_SQL_TIMEOUT_MS = 10_000;

/** How long the in-frame `sources.get` / `sources.fetch` waits for the studio's
 *  reply (D-WEB-19/23) — same posture as the sql timeout: reject kid-readably,
 *  never hang. */
export const SITE_SOURCE_TIMEOUT_MS = 10_000;

/** How many source calls (`sources.get` + `sources.fetch` COMBINED — one shared
 *  budget, D-WEB-23) may be in flight at once — parallel to the sql channel's
 *  MAX_PENDING_SQL (8). Enforced TWICE: in the frame's shim (an unawaited
 *  source loop in polite kid code fails locally, before a single postMessage
 *  leaves the sandbox) AND studio-side in SiteFrame (the trust fence — forged
 *  postMessages bypass the shim, and each forward is an authed POST that may
 *  hit a real upstream provider). */
export const MAX_SOURCE_INFLIGHT = 8;

/** The kid-readable rejection for a `sources.fetch` URL that is not an absolute
 *  `https://` address — used by BOTH fences (the in-frame shim rejects locally
 *  before any postMessage; the studio channel re-checks the untrusted wire). */
export const SOURCE_FETCH_URL_ERROR =
  "sources.fetch needs a full https:// web address — like sources.fetch('https://api.example.com/data').";

/** Serialize one DOM attribute for the studio skeleton. `&` is escaped BEFORE
 *  `"` so entity-like text survives the parse→serialize round trip unchanged
 *  (`&quot;` in the source must not come back as a literal quote). */
const serializeAttrs = (el: Element): string =>
  Array.from(el.attributes)
    .map(
      (a) =>
        ` ${a.name}="${a.value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')}"`,
    )
    .join('');

export interface BuildSiteOptions {
  /** Which VFS `.html` page is the document (default {@link SITE_HOME_PAGE}). */
  page?: string;
  /** Extra assets to inline that are NOT in the project VFS (class shared
   *  assets resolved to ready `data:` URLs) — same seam as the game builder. */
  virtualAssets?: VfsFile[];
  /**
   * Chatbot Studio ONLY (creative-code-studio-chatbot-prd D-CB-06): when set, the
   * `window.ai` chat bridge is injected after the site runtime. Absent (every
   * website / game caller) → the srcdoc is BYTE-IDENTICAL to before, so a plain
   * website can never become a chat-billing surface by accident.
   */
  chat?: BuildSiteChatOptions;
}

/** The bot's `bot.json` brain, as the page sees it (`ai.config`). */
export interface SiteChatConfig {
  name: string;
  greeting: string;
  starters: string[];
}

export interface BuildSiteChatOptions {
  /** Frozen copy of `bot.json` inlined as `ai.config`. */
  config: SiteChatConfig;
  /** Stars one reply costs right now — inlined as `ai.price()`. */
  price: number;
  /** The parent-owned transcript at BUILD time — the initial `ai.history()`
   *  mirror. Later replies update the mirror in-frame; the page is NOT rebuilt
   *  per message (a rebuild is a page reload). */
  history: ChatTranscriptItem[];
}

export interface SitePreview {
  srcDoc: string;
  /** Where each inlined kid script lives in the srcdoc (syntax-error mapping). */
  scriptRanges: ScriptLineRange[];
  /** The page actually rendered (the requested page, or the fallback). */
  currentPage: string;
}

/** The in-frame nav shim's message: the kid clicked a link to another page.
 *  Nothing else travels — the db is server-side (D-WEB-15), so navigation has
 *  no state to carry. */
export interface SiteNavigateMessage {
  path: string;
}

export function isSiteNavigateMessage(
  data: unknown,
): data is { __airbotixSiteNavigate: true } & SiteNavigateMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    '__airbotixSiteNavigate' in data &&
    (data as { __airbotixSiteNavigate: unknown }).__airbotixSiteNavigate === true &&
    typeof (data as { path?: unknown }).path === 'string'
  );
}

/** The in-frame shim's `db.query` request (frame → studio): SiteFrame proxies
 *  it to `POST /projects/:id/db/query` with the kid's session — the session
 *  token never enters the frame — and posts the {@link SiteSqlReply} back.
 *
 *  `token` is a PER-DOCUMENT nonce (minted at shim init): `id` alone cannot
 *  key a reply, because a srcdoc swap (live rebuild / page nav) keeps the SAME
 *  WindowProxy while the new document's counter restarts at 0 — a late reply
 *  for the OLD document would otherwise resolve the new document's same-id
 *  query with the wrong rows. The studio echoes the token; the shim ignores
 *  replies whose token is not its own. `params` is untrusted wire data — the
 *  studio validates the elements before anything reaches the backend. */
export interface SiteSqlRequest {
  id: number;
  token: string;
  sql: string;
  params: unknown[];
}

export function isSiteSqlRequest(
  data: unknown,
): data is { __airbotixSiteControl: true; action: 'sql' } & SiteSqlRequest {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as {
    __airbotixSiteControl?: unknown;
    action?: unknown;
    id?: unknown;
    token?: unknown;
    sql?: unknown;
  };
  return (
    m.__airbotixSiteControl === true &&
    m.action === 'sql' &&
    typeof m.id === 'number' &&
    typeof m.token === 'string' &&
    typeof m.sql === 'string'
  );
}

/** The studio's reply to a {@link SiteSqlRequest} (studio → frame), echoing
 *  the request's `id` + per-document `token`. `result` is the backend query
 *  envelope on success; `error` is the backend's kid-readable message on
 *  failure. */
export interface SiteSqlReply {
  __airbotixSiteSql: true;
  id: number;
  token: string;
  ok: boolean;
  result?: {
    columns: string[];
    rows: Record<string, unknown>[];
    row_count: number;
    changes: number | null;
    last_insert_rowid: string | null;
  };
  error?: string;
}

/** The in-frame shim's `sources.get` request (frame → studio, D-WEB-19):
 *  SiteFrame proxies it to `POST /projects/:id/sources/:name` with the kid's
 *  session — the frame stays token-free and the sandbox keeps ZERO egress; the
 *  BACKEND fetches the curated external provider. Rides the SAME per-document
 *  token the sql channel mints (one scheme, one srcdoc-swap guard). `params`
 *  is untrusted wire data — the studio validates the values (scalars only)
 *  before anything reaches the backend. */
export interface SiteSourceRequest {
  id: number;
  token: string;
  name: string;
  params: Record<string, unknown>;
}

export function isSiteSourceRequest(
  data: unknown,
): data is { __airbotixSiteControl: true; action: 'source' } & SiteSourceRequest {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as {
    __airbotixSiteControl?: unknown;
    action?: unknown;
    id?: unknown;
    token?: unknown;
    name?: unknown;
  };
  return (
    m.__airbotixSiteControl === true &&
    m.action === 'source' &&
    typeof m.id === 'number' &&
    typeof m.token === 'string' &&
    typeof m.name === 'string'
  );
}

/** The in-frame shim's `sources.fetch` request (frame → studio, D-WEB-23):
 *  the OPEN door — any public `https://` JSON API, no catalog entry needed.
 *  A sibling action to `'source'` riding the SAME per-document token, id
 *  sequence, shared in-flight cap and {@link SiteSourceReply} envelope; the
 *  studio forwards it to `POST /projects/:id/sources/fetch` (or the per-share
 *  variant) and the BACKEND fetches the URL — the sandbox keeps ZERO egress.
 *  `url` is untrusted wire data — the studio re-validates it (absolute
 *  https:// string) before anything reaches the backend. */
export interface SiteSourceFetchRequest {
  id: number;
  token: string;
  url: string;
}

export function isSiteSourceFetchRequest(
  data: unknown,
): data is { __airbotixSiteControl: true; action: 'source-fetch' } & SiteSourceFetchRequest {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as {
    __airbotixSiteControl?: unknown;
    action?: unknown;
    id?: unknown;
    token?: unknown;
    url?: unknown;
  };
  return (
    m.__airbotixSiteControl === true &&
    m.action === 'source-fetch' &&
    typeof m.id === 'number' &&
    typeof m.token === 'string' &&
    typeof m.url === 'string'
  );
}

/** The studio's reply to a {@link SiteSourceRequest} OR a
 *  {@link SiteSourceFetchRequest} (studio → frame), echoing `id` + the
 *  per-document `token`. `data` is the source's JSON on success; `error` is
 *  the kid-readable message (the backend's SOURCE_* messages are surfaced
 *  verbatim) on failure. */
export interface SiteSourceReply {
  __airbotixSiteSource: true;
  id: number;
  token: string;
  ok: boolean;
  data?: unknown;
  error?: string;
}

// ── The chat bridge (Chatbot Studio, creative-code-studio-chatbot-prd §3.2) ──

/** How long the in-frame `ai.chat` waits for the studio's reply (an LLM reply
 *  can take a while; a wedged studio must still fail kid-readably, never hang). */
export const SITE_CHAT_TIMEOUT_MS = 45_000;
/** ONE message at a time per frame — a second `ai.chat` while busy rejects BUSY. */
export const MAX_PENDING_CHAT = 1;
/** Longest message the page may send (server-validated too). */
export const CHAT_MESSAGE_MAX_CHARS = 500;

/** The typed error codes `ai.chat` rejects with — the starter script.js maps each
 *  to a kid line. The set is a CONTRACT with the backend coach prompt and the
 *  templates (platform-backend `chatbot-prompt.ts`) — never rename one. */
export type SiteChatErrorCode =
  | 'OUT_OF_STARS'
  | 'BUDGET_EXHAUSTED'
  | 'DAILY_CAP'
  | 'MESSAGE_BLOCKED'
  | 'VIEW_ONLY'
  | 'AI_UNAVAILABLE'
  | 'PAUSED'
  | 'BUSY';

/** What `ai.chat` resolves with (the backend reply envelope, minus `session`). */
export interface SiteChatResult {
  reply: string;
  stars_charged: number;
  /** Studio replies: the kid's wallet balance after the debit. */
  balance_after?: number;
  /** Public (/play) replies: visitor Stars left on the shared bot's budget. */
  budget_remaining?: number;
}

/** The in-frame `ai.chat` request (frame → parent): the PARENT forwards it to the
 *  authed studio endpoint or the public per-share endpoint (the frame never holds
 *  a token) and appends both lines to the device-local transcript. `text` is
 *  untrusted wire data — the parent re-validates it. */
export interface SiteChatRequest {
  id: number;
  token: string;
  text: string;
}

export function isSiteChatRequest(
  data: unknown,
): data is { __airbotixSiteControl: true; action: 'chat' } & SiteChatRequest {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as {
    __airbotixSiteControl?: unknown;
    action?: unknown;
    id?: unknown;
    token?: unknown;
    text?: unknown;
  };
  return (
    m.__airbotixSiteControl === true &&
    m.action === 'chat' &&
    typeof m.id === 'number' &&
    typeof m.token === 'string' &&
    typeof m.text === 'string'
  );
}

/** The in-frame `ai.clear()` request — wipes the parent-owned transcript. */
export interface SiteChatClearRequest {
  id: number;
  token: string;
}

export function isSiteChatClearRequest(
  data: unknown,
): data is { __airbotixSiteControl: true; action: 'chat-clear' } & SiteChatClearRequest {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as { __airbotixSiteControl?: unknown; action?: unknown; id?: unknown; token?: unknown };
  return (
    m.__airbotixSiteControl === true &&
    m.action === 'chat-clear' &&
    typeof m.id === 'number' &&
    typeof m.token === 'string'
  );
}

/** The parent's reply to a chat / chat-clear request (parent → frame), echoing
 *  `id` + the per-document `token`. `result` is the reply envelope (chat) or
 *  `null` (clear); `error` carries the typed code + a kid-readable message. */
export interface SiteChatReply {
  __airbotixSiteChat: true;
  id: number;
  token: string;
  ok: boolean;
  result?: SiteChatResult | null;
  error?: { code: SiteChatErrorCode; message: string };
}

/**
 * The `window.ai` bridge (Chatbot Studio only, D-CB-06): `ai.config` (frozen
 * bot.json), `ai.history()` (the transcript mirror the parent seeded at build
 * time and the bridge keeps in step), `await ai.chat(text)` (ONE message → the
 * parent → the backend; resolves with the reply envelope; rejects with an
 * Error carrying a typed `code`), `await ai.clear()`, `ai.price()`. Same
 * per-document token posture as the sql/source channels: a srcdoc swap restarts
 * the id counter, so replies must echo this document's nonce.
 */
function siteChatRuntime(chat: BuildSiteChatOptions): string {
  const config: SiteChatConfig = {
    name: String(chat.config.name),
    greeting: String(chat.config.greeting),
    starters: chat.config.starters.map(String),
  };
  const history = chat.history.map(({ role, text, at }) => ({ role, text, at }));
  return `<script>
(function () {
  var chatToken = 'c' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  var chatSeq = 0;
  var chatPending = {};
  var chatInflight = 0;
  var config = JSON.parse(${jsStr(JSON.stringify(config))});
  var history = JSON.parse(${jsStr(JSON.stringify(history))});
  var price = ${Number.isFinite(chat.price) ? Math.max(0, Math.floor(chat.price)) : 0};
  function chatError(code, message) {
    var err = new Error(message);
    err.code = code;
    return err;
  }
  // A sandbox without allow-forms can stop native form submission before the
  // page's submit listener runs. Chatbot pages use that listener to call
  // ai.chat, so convert the two user activation paths into a synthetic submit
  // while still cancelling every native submission. The strict sandbox stays
  // unchanged and the kid's own submit handler remains the UI owner.
  function dispatchChatbotSubmit(form, submitter) {
    if (!form) return;
    var event;
    try {
      event = new SubmitEvent('submit', {
        bubbles: true,
        cancelable: true,
        submitter: submitter || undefined
      });
    } catch (err) {
      event = document.createEvent('Event');
      event.initEvent('submit', true, true);
      try { Object.defineProperty(event, 'submitter', { value: submitter || null }); } catch (defineErr) {}
    }
    form.dispatchEvent(event);
  }
  function findSubmitter(target) {
    var node = target;
    while (node && node !== document) {
      var tag = node.tagName && String(node.tagName).toLowerCase();
      if (tag === 'button' || tag === 'input') {
        var fallback = tag === 'button' ? 'submit' : 'text';
        var type = String(node.getAttribute('type') || fallback).toLowerCase();
        if ((type === 'submit' || type === 'image') && node.form && !node.disabled) return node;
      }
      node = node.parentElement;
    }
    return null;
  }
  document.addEventListener('submit', function (event) {
    // This capture listener is the security boundary: no chatbot form may
    // attempt native navigation, including forms added after page load.
    event.preventDefault();
  }, true);
  document.addEventListener('click', function (event) {
    var submitter = findSubmitter(event.target);
    if (!submitter) return;
    event.preventDefault();
    dispatchChatbotSubmit(submitter.form, submitter);
  }, true);
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    var input = event.target;
    if (!input || !input.tagName || String(input.tagName).toLowerCase() !== 'input') return;
    var type = String(input.getAttribute('type') || 'text').toLowerCase();
    if (type === 'button' || type === 'submit' || type === 'reset' || type === 'image' || type === 'file') return;
    if (!input.form || input.disabled || input.readOnly) return;
    event.preventDefault();
    var controls = input.form.querySelectorAll('button, input');
    var submitter = null;
    for (var i = 0; i < controls.length; i += 1) {
      submitter = findSubmitter(controls[i]);
      if (submitter) break;
    }
    dispatchChatbotSubmit(input.form, submitter);
  }, true);
  function post(fields, onOk, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var id = ++chatSeq;
      var timer = setTimeout(function () {
        delete chatPending[id];
        chatInflight -= 1;
        reject(chatError('AI_UNAVAILABLE', 'The bot took too long to answer — try again in a moment.'));
      }, timeoutMs);
      chatPending[id] = { resolve: resolve, reject: reject, timer: timer, onOk: onOk };
      chatInflight += 1;
      var msg = { __airbotixSiteControl: true, id: id, token: chatToken };
      for (var k in fields) msg[k] = fields[k];
      parent.postMessage(msg, '*');
    });
  }
  window.ai = {
    config: Object.freeze({ name: config.name, greeting: config.greeting, starters: config.starters.slice() }),
    history: function () { return history.slice(); },
    price: function () { return { stars: price }; },
    chat: function (text) {
      var clean = typeof text === 'string' ? text.trim() : '';
      if (!clean) return Promise.reject(chatError('MESSAGE_BLOCKED', 'Type a message first.'));
      if (clean.length > ${CHAT_MESSAGE_MAX_CHARS}) {
        return Promise.reject(chatError('MESSAGE_BLOCKED', 'Keep each message under ${CHAT_MESSAGE_MAX_CHARS} characters.'));
      }
      if (chatInflight >= ${MAX_PENDING_CHAT}) {
        return Promise.reject(chatError('BUSY', 'One message at a time — wait for the reply!'));
      }
      return post({ action: 'chat', text: clean }, function (result) {
        history.push({ role: 'user', text: clean, at: Date.now() });
        history.push({ role: 'bot', text: String(result && result.reply), at: Date.now() });
        if (history.length > ${CHATBOT_TRANSCRIPT_CAP}) history = history.slice(-${CHATBOT_TRANSCRIPT_CAP});
      }, ${SITE_CHAT_TIMEOUT_MS});
    },
    clear: function () {
      return post({ action: 'chat-clear' }, function () { history = []; }, ${SITE_SOURCE_TIMEOUT_MS});
    }
  };
  window.addEventListener('message', function (e) {
    var m = e.data;
    if (!m || m.__airbotixSiteChat !== true) return;
    if (m.token !== chatToken) return; // a reply for another document — not ours
    var pending = chatPending[m.id];
    if (!pending) return; // timed out / not ours
    delete chatPending[m.id];
    chatInflight -= 1;
    clearTimeout(pending.timer);
    if (m.ok) {
      try { pending.onOk(m.result); } catch (err) {}
      pending.resolve(m.result);
    } else {
      var code = m.error && m.error.code ? String(m.error.code) : 'AI_UNAVAILABLE';
      var text = m.error && m.error.message ? String(m.error.message) : 'The bot could not answer.';
      pending.reject(chatError(code, text));
    }
  });
})();
</script>`;
}

/**
 * The site runtime shim (contract items the backend agent teaches verbatim):
 * `db.query(sql, ...params)` — REAL SQL on the project's server-side SQLite db
 * (D-WEB-15), each query posted to the parent studio over the sql postMessage
 * channel (the frame stays token-free; the studio calls the backend with the
 * kid's session) — `sources.get(name, params)`, curated EXTERNAL data via the
 * server proxy (D-WEB-19; same token machinery, `action:'source'`, in-frame
 * in-flight cap) — `sources.fetch(url)`, ANY public https JSON API via the
 * same proxy (D-WEB-23; `action:'source-fetch'`, SHARED in-flight cap, local
 * https-URL check) — `app.get/app.post` route registration, the /api-only fetch
 * shim (query/body parsing, chainable `res.status().json()`, ASYNC handlers
 * awaited, kid-friendly 404/500 console errors, everything else blocked), the
 * capture-phase nav shim that posts relative `*.html` link clicks to the
 * studio (no state rides along — the db is server-side and simply persists),
 * and a kid-readable reporter for silent CSP violations.
 *
 * Run-report instrumentation (D-WEB-13): the shim ALSO keeps evidence ledgers —
 * every /api fetch it served/blocked (with the real status; 0 = rejected before
 * any response), which elements kid code attached click/pointerdown/submit
 * listeners to (via an `EventTarget.prototype.addEventListener` wrap installed
 * LAST, so the shim's own listeners never count), whether document/body got a
 * delegated click handler, and whether the page finished loading. A
 * `{__airbotixControl, action:'report'}` request (the same control channel the
 * game probe uses) replies `{__airbotixSiteReport, site}` with those ledgers
 * plus the visible interactive elements cross-checked for `wired`. The parent
 * collector re-clamps everything — this frame is untrusted.
 */
function siteRuntime(page: string): string {
  return `<script>
(function () {
  // ── db.query: REAL SQL on the project's server-side database (D-WEB-15) ────
  // Params: db.query(sql, a, b) and db.query(sql, [a, b]) both work.
  //
  // RETURN SHAPE (the Web Critter prompt teaches EXACTLY this — keep in sync):
  //   - a READER statement (the reply carries columns, e.g. SELECT) resolves
  //     with the ROWS ARRAY:      var pets = await db.query('SELECT * FROM pets');
  //   - a WRITE statement (no columns: INSERT/UPDATE/DELETE/CREATE …) resolves
  //     with { changes, lastInsertRowid }.
  // An SQL error rejects with the server's kid-readable message AND
  // console.errors it verbatim (feeds the D-WEB-13 logs ledger).
  var sqlSeq = 0;
  var sqlPending = {};
  // Per-DOCUMENT nonce (shared by the sql AND sources channels — one scheme):
  // a srcdoc swap keeps the same WindowProxy but restarts the id counters, so
  // id alone could match a LATE reply meant for the previous document (wrong
  // rows for a same-id query). Replies must echo this token.
  var docToken = Math.random().toString(36).slice(2) + Date.now().toString(36);
  window.db = {
    query: function (sql) {
      var params = Array.prototype.slice.call(arguments, 1);
      if (params.length === 1 && Array.isArray(params[0])) params = params[0];
      return new Promise(function (resolve, reject) {
        var id = ++sqlSeq;
        var timer = setTimeout(function () {
          delete sqlPending[id];
          var msg = 'Your database took too long to answer (10 seconds) — try again, or reload your site.';
          console.error(msg);
          reject(new Error(msg));
        }, ${SITE_SQL_TIMEOUT_MS});
        sqlPending[id] = { resolve: resolve, reject: reject, timer: timer };
        parent.postMessage({ __airbotixSiteControl: true, action: 'sql', id: id, token: docToken, sql: String(sql), params: params }, '*');
      });
    }
  };
  window.addEventListener('message', function (e) {
    var m = e.data;
    if (!m || m.__airbotixSiteSql !== true) return;
    if (m.token !== docToken) return; // a reply for another document — not ours
    var pending = sqlPending[m.id];
    if (!pending) return; // timed out / not ours
    delete sqlPending[m.id];
    clearTimeout(pending.timer);
    if (m.ok) {
      var r = m.result || {};
      if (r.columns && r.columns.length > 0) pending.resolve(r.rows || []);
      else pending.resolve({ changes: r.changes == null ? 0 : r.changes, lastInsertRowid: r.last_insert_rowid == null ? null : r.last_insert_rowid });
    } else {
      var msg = typeof m.error === 'string' && m.error !== '' ? m.error : 'Your SQL could not run.';
      console.error(msg);
      pending.reject(new Error(msg));
    }
  });

  // ── sources: EXTERNAL data, server-proxied — get (curated catalog, D-WEB-19)
  // + fetch (ANY public https JSON API, D-WEB-23) ─────────────────────────────
  // await sources.get('weather', { city: 'Sydney' }) → the parent studio calls
  // POST /projects/:id/sources/:name; await sources.fetch('https://…') → the
  // parent calls POST /projects/:id/sources/fetch — either way the BACKEND
  // fetches the real provider with the kid's session and the sandbox itself
  // keeps ZERO egress. Both resolve with the JSON data; an error rejects with
  // the server's kid-readable message AND console.errors it verbatim (D-WEB-13
  // logs ledger). Same per-document token as db.query (one reply-matching
  // scheme); ONE shared in-flight budget across get + fetch.
  var srcSeq = 0;
  var srcPending = {};
  var srcInflight = 0;
  function srcCall(fields) {
    return new Promise(function (resolve, reject) {
      // Local backpressure: an unawaited source loop fails HERE, kid-readably,
      // before a single request leaves the frame.
      if (srcInflight >= ${MAX_SOURCE_INFLIGHT}) {
        var busy = 'Too many data-source requests at once (over ${MAX_SOURCE_INFLIGHT}) — await each sources.get or sources.fetch before starting the next.';
        console.error(busy);
        reject(new Error(busy));
        return;
      }
      var id = ++srcSeq;
      srcInflight += 1;
      var timer = setTimeout(function () {
        delete srcPending[id];
        srcInflight -= 1;
        var msg = 'That data source took too long to answer (10 seconds) — try again in a moment.';
        console.error(msg);
        reject(new Error(msg));
      }, ${SITE_SOURCE_TIMEOUT_MS});
      srcPending[id] = { resolve: resolve, reject: reject, timer: timer };
      var post = { __airbotixSiteControl: true, id: id, token: docToken };
      for (var k in fields) post[k] = fields[k];
      parent.postMessage(post, '*');
    });
  }
  window.sources = {
    get: function (name, params) {
      return srcCall({ action: 'source', name: String(name), params: params || {} });
    },
    fetch: function (url) {
      // Absolute https:// URLs only — anything else fails locally, kid-readably,
      // without touching the backend (the studio re-checks; this is UX).
      if (typeof url !== 'string' || url.slice(0, 8) !== 'https://' || url.length <= 8) {
        var bad = ${jsStr(SOURCE_FETCH_URL_ERROR)};
        console.error(bad);
        return Promise.reject(new Error(bad));
      }
      return srcCall({ action: 'source-fetch', url: url });
    }
  };
  window.addEventListener('message', function (e) {
    var m = e.data;
    if (!m || m.__airbotixSiteSource !== true) return;
    if (m.token !== docToken) return; // a reply for another document — not ours
    var pending = srcPending[m.id];
    if (!pending) return; // timed out / not ours
    delete srcPending[m.id];
    srcInflight -= 1;
    clearTimeout(pending.timer);
    if (m.ok) {
      pending.resolve(m.data);
    } else {
      var msg = typeof m.error === 'string' && m.error !== '' ? m.error : 'That data source could not answer.';
      console.error(msg);
      pending.reject(new Error(msg));
    }
  });

  // ── run-report evidence ledgers (D-WEB-13) — read by the 'report' request ──
  var evidence = { pageLoaded: false, api: [], wired: null, delegated: false };
  try { evidence.wired = new WeakSet(); } catch (e) { evidence.wired = null; }
  // Loaded = DOM parsed + kid scripts executed (they're inline, so they've all
  // run by DOMContentLoaded). The shim lives in <head>, so readyState is
  // normally 'loading' here; the direct check covers late execution.
  if (document.readyState !== 'loading') { evidence.pageLoaded = true; }
  else { document.addEventListener('DOMContentLoaded', function () { evidence.pageLoaded = true; }); }
  function recordApi(method, path, status) {
    if (evidence.api.length < 30) {
      evidence.api.push({ method: method, path: String(path).slice(0, 120), status: status });
    }
  }

  // ── app: the route table server.js registers into ──────────────────────────
  var routes = { GET: {}, POST: {} };
  window.app = {
    get: function (path, handler) { routes.GET[path] = handler; },
    post: function (path, handler) { routes.POST[path] = handler; }
  };
  function matchRoute(table, path) {
    if (!table) return null;
    if (table[path]) return { handler: table[path], params: {} };
    var actual = path.split('/');
    var patterns = Object.keys(table);
    for (var r = 0; r < patterns.length; r++) {
      var pattern = patterns[r];
      var expected = pattern.split('/');
      if (expected.length !== actual.length) continue;
      var params = {};
      var matches = true;
      for (var s = 0; s < expected.length; s++) {
        if (expected[s].charAt(0) === ':' && expected[s].length > 1) {
          try { params[expected[s].slice(1)] = decodeURIComponent(actual[s]); }
          catch (e) { params[expected[s].slice(1)] = actual[s]; }
        } else if (expected[s] !== actual[s]) {
          matches = false;
          break;
        }
      }
      if (matches) return { handler: table[pattern], params: params };
    }
    return null;
  }

  // ── fetch: serves ONLY the site's own /api/* routes — no real network I/O ──
  function toResponse(status, data) {
    var body = JSON.stringify(data === undefined ? null : data);
    var ok = status >= 200 && status < 300;
    try {
      return new Response(body, { status: status, headers: { 'Content-Type': 'application/json' } });
    } catch (e) {
      return {
        ok: ok,
        status: status,
        json: function () { return Promise.resolve(JSON.parse(body)); },
        text: function () { return Promise.resolve(body); }
      };
    }
  }
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url ? input.url : String(input));
    var method = (init && init.method ? String(init.method) : 'GET').toUpperCase();
    // A project FILE (fetch('assets/imported/levels.json')) is served from the
    // asset-origin shim's inlined bytes — never a network request.
    try {
      var ao = window.${ASSET_ORIGIN_GLOBAL};
      var hit = ao && ao.fetch ? ao.resolveOrWarn(url) : null;
      if (hit) return ao.fetch(hit, init);
    } catch (e) {}
    if (url.slice(0, 5) !== '/api/') {
      recordApi(method, url, 0); // 0 = rejected before any response
      console.error('fetch("' + url + '") was blocked — the sandbox blocks the outside internet. Your site can only fetch its own /api/... routes, but it CAN reach outside through the studio — try await sources.fetch(\\'https://…\\') for any public API, or await sources.get(...) for the studio data sources.');
      return Promise.reject(new TypeError('Failed to fetch: ' + url));
    }
    var qi = url.indexOf('?');
    var path = qi === -1 ? url : url.slice(0, qi);
    var query = {};
    if (qi !== -1) {
      var pairs = url.slice(qi + 1).split('&');
      for (var p = 0; p < pairs.length; p++) {
        if (!pairs[p]) continue;
        var eq = pairs[p].indexOf('=');
        var k = eq === -1 ? pairs[p] : pairs[p].slice(0, eq);
        var v = eq === -1 ? '' : pairs[p].slice(eq + 1);
        try { query[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\\+/g, ' ')); }
        catch (e) { query[k] = v; }
      }
    }
    var body = null;
    if (init && init.body != null) {
      try { body = JSON.parse(String(init.body)); } catch (e) { body = null; }
    }
    var matchedRoute = matchRoute(routes[method], path);
    var handler = matchedRoute && matchedRoute.handler;
    return new Promise(function (resolve) {
      if (!handler) {
        recordApi(method, path, 404);
        var pathSegments = path.split('/');
        if (pathSegments.indexOf('undefined') !== -1 || pathSegments.indexOf('null') !== -1) {
          console.error('No route answered ' + method + ' ' + path + ' because the page built the URL with a missing value. Fix the frontend value used in fetch(...) and make sure that field exists in the seed/database row; do not add an undefined or null route.');
        } else {
          console.error('No route answered ' + method + ' ' + path + " — add a matching app." + method.toLowerCase() + "('/api/path', ...) route in server.js. Dynamic segments such as /api/post/:id are supported through req.params.");
        }
        resolve(toResponse(404, { error: 'No route for ' + method + ' ' + path }));
        return;
      }
      var req = { method: method, path: path, query: query, params: matchedRoute.params, body: body };
      var status = 200;
      var recorded = false; // ledger once per call, whichever path answers first
      var res = {
        status: function (code) { status = code; return res; },
        json: function (data) {
          if (!recorded) { recorded = true; recordApi(method, path, status); }
          resolve(toResponse(status, data));
        }
      };
      var crash = function (err) {
        if (!recorded) { recorded = true; recordApi(method, path, 500); }
        console.error('Your ' + method + ' ' + path + ' route crashed: ' + (err && err.message ? err.message : err));
        resolve(toResponse(500, { error: String(err && err.message ? err.message : err) }));
      };
      // Handlers may be ASYNC (they await db.query) — the fetch settles only
      // when res.json fires (possibly after awaits), and a rejected handler
      // promise answers 500 with the real ledger status, same as a sync throw.
      try {
        var out = handler(req, res);
        if (out && typeof out.then === 'function') { out.then(null, crash); }
      }
      catch (err) { crash(err); }
    });
  };

  // ── nav: relative *.html link clicks post a navigation to the studio ───────
  document.addEventListener('click', function (e) {
    var el = e.target;
    while (el && !(el.tagName && el.tagName.toUpperCase() === 'A')) el = el.parentElement;
    if (!el) return;
    var href = el.getAttribute('href');
    if (href == null || href === '') return;
    if (href.charAt(0) === '#') return; // same-page anchor — native scroll is fine
    var clean = href.split('#')[0].split('?')[0];
    if (clean.slice(0, 2) === './') clean = clean.slice(2);
    var external = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(clean) || clean.slice(0, 2) === '//' || clean.charAt(0) === '/';
    if (external || !/\\.html$/i.test(clean)) {
      e.preventDefault();
      console.info('Only links to your own pages (like "about.html") work here.');
      return;
    }
    e.preventDefault();
    parent.postMessage({ __airbotixSiteNavigate: true, path: clean }, '*');
  }, true);

  // ── CSP violations → a kid-readable console line ───────────────────────────
  // The deny-by-default CSP silently drops blocked subresources: without this a
  // kid sees a broken picture and an EMPTY console (violations don't surface as
  // console errors the capture shim can see). Content-free on purpose — the
  // blocked directive only, never the URL (it can carry page data / PII).
  var CSP_HINTS = {
    'img-src': 'That picture link is blocked — a picture from the web needs a full https:// link (http:// and other kinds are blocked), or import the picture into your project and use its file name.',
    'media-src': 'Sound and video from the internet are blocked — import the file into your project, then use its file name.',
    'font-src': 'Fonts from the internet are blocked — use a normal system font in your CSS instead.',
    'script-src': 'Loading code from the internet is blocked — put your code in a .js file in your project instead.',
    'style-src': 'Loading styles from the internet is blocked — put your styles in style.css instead.',
    'connect-src': 'Your site can only talk to its OWN backend with fetch("/api/...") and the outside internet through the studio — sources.fetch("https://…") or sources.get(...). Direct connections are blocked.',
    'frame-src': 'Putting another website inside your page is blocked.',
    'default-src': 'That came from the internet, which is blocked here — keep everything inside your project.'
  };
  // A blocked URL on the studio's OWN origin is NOT the internet: it is a
  // relative path that matched no project file, which the srcdoc sandbox
  // resolved against the studio page. Say so — the "internet" hint sent kids
  // who had ALREADY imported the picture round in circles. Still content-free:
  // only the origin is compared, the URL itself is never logged.
  var OWN_HINTS = {
    'img-src': 'That picture is not a file in your project — the path in your code matches nothing in My assets. Import it there, then use its exact path (like assets/imported/cat.png).',
    'media-src': 'That sound or video is not a file in your project — the path in your code matches nothing in My assets. Import it there, then use its exact path.',
    'font-src': 'That font file is not in your project — use a normal system font in your CSS instead.'
  };
  var ownOrigin = null;
  try { ownOrigin = new URL(document.baseURI).origin; } catch (err) {}
  window.addEventListener('securitypolicyviolation', function (e) {
    try {
      var directive = String((e && (e.effectiveDirective || e.violatedDirective)) || 'default-src').split(' ')[0];
      var blocked = String((e && e.blockedURI) || '');
      var own = typeof ownOrigin === 'string' && ownOrigin !== 'null' && blocked.indexOf(ownOrigin + '/') === 0;
      console.error((own && OWN_HINTS[directive]) || CSP_HINTS[directive] || CSP_HINTS['default-src']);
    } catch (err) {}
  });

  // ── report: the studio's run probe (D-WEB-13) ──────────────────────────────
  // Replies with the evidence ledgers + the VISIBLE interactive elements
  // cross-checked against the listener ledger. Selector: #id when the element
  // has one, else tag.firstClass best-effort. With no ledger (no WeakSet) we
  // never guess AGAINST the kid: everything reports wired.
  window.addEventListener('message', function (e) {
    var m = e.data;
    if (!m || m.__airbotixControl !== true || m.action !== 'report') return;
    var buttons = [];
    try {
      var els = document.querySelectorAll('button, input[type="button"], input[type="submit"], [role="button"]');
      for (var bi = 0; bi < els.length && buttons.length < 30; bi++) {
        var el = els[bi];
        if (!el.getClientRects || el.getClientRects().length === 0) continue; // hidden
        var sel = el.id
          ? '#' + el.id
          : el.tagName.toLowerCase() + (el.classList && el.classList.length > 0 ? '.' + el.classList[0] : '');
        var isWired = evidence.wired === null || evidence.wired.has(el) ||
          (el.form != null && evidence.wired.has(el.form)) ||
          typeof el.onclick === 'function' ||
          (el.form != null && typeof el.form.onsubmit === 'function');
        buttons.push({ selector: String(sel).slice(0, 80), wired: isWired });
      }
    } catch (err) { buttons = []; }
    parent.postMessage({ __airbotixSiteReport: true, site: {
      pageLoaded: evidence.pageLoaded,
      page: ${jsStr(page)},
      apiCalls: evidence.api,
      buttons: buttons,
      delegatedClickHandler: evidence.delegated
    } }, '*');
  });

  // ── listener ledger (D-WEB-13) — installed LAST, deliberately ──────────────
  // Only registrations made AFTER this point (i.e. by kid scripts) land in the
  // ledger. The shim's OWN document click (nav) / message listeners above must
  // never count, or delegatedClickHandler would be true for EVERY site and the
  // unwired-button evidence — the "Add task does nothing" signature — would be
  // permanently suppressed.
  try {
    var addListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (type, listener, options) {
      try {
        if (type === 'click' || type === 'pointerdown' || type === 'submit') {
          if (type === 'click' && (this === document || (document.body != null && this === document.body))) {
            evidence.delegated = true;
          }
          if (evidence.wired !== null && this instanceof Element) evidence.wired.add(this);
        }
      } catch (err) {}
      return addListener.call(this, type, listener, options);
    };
  } catch (e) {}
})();
</script>`;
}

/** Find a text `.html` VFS file at exactly this (normalized) path. */
function htmlFile(files: VfsFile[], path: string): VfsFile | undefined {
  return files.find((f) => f.kind === 'text' && f.path === path && /\.html$/i.test(f.path));
}

/** Friendly kid-facing empty state when the project has no index.html at all. */
function emptyStatePreview(): SitePreview {
  const srcDoc = [
    '<!doctype html>',
    '<html><head><meta charset="utf-8">',
    EXTENSION_NOISE_GUARD,
    CONSOLE_CAPTURE,
    CSP_META,
    '<style>body{margin:0;display:grid;place-items:center;min-height:100vh;font-family:system-ui,sans-serif;background:#f8fafc;color:#334155;text-align:center}</style>',
    '</head><body>',
    '<div><div style="font-size:56px">🌐</div><h1 style="font-size:20px">No pages yet!</h1><p>Ask your AI helper to build your first page (index.html).</p></div>',
    '</body></html>',
  ].join('\n');
  return { srcDoc, scriptRanges: [], currentPage: SITE_HOME_PAGE };
}

/** Match `<script src="…"></script>` reference tags (the runtime contract's
 *  plain relative form; anything the regex catches is replaced — an external
 *  URL is thereby neutralized into a console.error instead of loading). */
const SCRIPT_REF_RE = /<script\b[^>]*\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>\s*<\/script>/gi;
/** Match any `<link …>` tag; stylesheet ones are inlined, others left alone. */
const LINK_TAG_RE = /<link\b[^>]*>/gi;

/** Media a site references by project path — the references the runtime inlines. */
const MEDIA_EXT_RE = /\.(png|jpe?g|gif|webp|svg|avif|bmp|mp3|wav|ogg|m4a|mp4|webm|glb|ttf|otf|woff2?)$/i;
/** `src=` / `poster=` / `href=` attribute values — quoted or bare. */
const REF_ATTR_RE = /\b(?:src|poster|href)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/gi;
/** CSS `url(…)` — quoted or bare. */
const CSS_URL_RE = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^"')\s]*))\s*\)/gi;
/** A short quoted literal in kid JS (a path never spans a line). */
const JS_LITERAL_RE = /(["'`])([^"'`\n]{1,300})\1/g;
/** How many missing-media lines a build reports (a runaway page stays readable). */
const MAX_MISSING_MEDIA = 10;

/**
 * Media references that look like PROJECT files but match none — collected
 * AFTER inlining, so every resolved reference is already a `data:` URL and
 * skipped. Absolute URLs are never listed (the CSP judges those at run time).
 * Order = page, then stylesheets, then scripts (first spelling wins);
 * deduplicated; capped at {@link MAX_MISSING_MEDIA}.
 */
export function findUnresolvedMediaRefs(
  texts: ReadonlyArray<{ text: string; kind: 'html' | 'css' | 'js' }>,
  assets: VfsFile[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const consider = (ref: string | undefined): void => {
    if (!ref) return;
    const key = normalizeAssetRef(ref);
    if (key === null || !MEDIA_EXT_RE.test(key) || seen.has(key)) return;
    seen.add(key);
    if (out.length < MAX_MISSING_MEDIA && !findAssetByRef(ref, assets)) out.push(ref.trim());
  };
  for (const { text, kind } of texts) {
    if (kind === 'js') {
      for (const m of text.matchAll(JS_LITERAL_RE)) consider(m[2]);
      continue;
    }
    if (kind === 'html') {
      for (const m of text.matchAll(REF_ATTR_RE)) consider(m[1] ?? m[2] ?? m[3]);
    }
    for (const m of text.matchAll(CSS_URL_RE)) consider(m[1] ?? m[2] ?? m[3]);
  }
  return out;
}

export function buildSitePreview(files: VfsFile[], opts: BuildSiteOptions = {}): SitePreview {
  // Real VFS assets PLUS virtual class-shared assets — inlined identically to
  // the game builder (a data: URL rewritten into the quoted path).
  const assets = [...files.filter((f) => f.kind === 'asset'), ...(opts.virtualAssets ?? [])];

  // 1. Page selection: requested → index.html fallback → friendly empty state.
  const requested = normalizeRef(opts.page ?? SITE_HOME_PAGE);
  const pageFile = htmlFile(files, requested) ?? htmlFile(files, SITE_HOME_PAGE);
  if (!pageFile) return emptyStatePreview();

  // Each inlined kid script, keyed by its exact final tag text — located in the
  // assembled srcdoc afterwards to compute line ranges (dedup: a page that
  // references the same file twice reuses one entry, every occurrence ranged).
  const injected = new Map<string, { path: string; content: string }>();
  // Every kid text that reaches the frame, post-inlining, for the missing-media
  // scan (a reference the inliner resolved is a data: URL by now — skipped).
  const scanned: Array<{ text: string; kind: 'html' | 'css' | 'js' }> = [];
  const kidScriptTag = (f: VfsFile): string => {
    // ⚠️ Content starts IMMEDIATELY after `<script>` — no leading newline — so
    // sourceURL line numbers match the kid's file (same contract as the game).
    // A literal `</script>` in kid text is escaped (same line count) so it
    // can't truncate the tag and corrupt every scriptRange after it.
    const inlined = inlineAssetRefs(f.content, assets);
    scanned.push({ text: inlined, kind: 'js' });
    const content = escapeScriptText(inlined);
    const tag = `<script>${content}\n//# sourceURL=${f.path}\n${'<'}/script>`;
    injected.set(tag, { path: f.path, content });
    return tag;
  };
  const missingRefTag = (ref: string): string =>
    `<script>console.error(${jsStr(
      `Missing file "${ref}" — this page references it but it isn't in your project (outside URLs are blocked).`,
    )});${'<'}/script>`;

  // server.js — ALWAYS injected first among kid scripts (it defines the routes),
  // whether or not the page references it.
  const server = files.find((f) => f.kind === 'text' && f.path === SERVER_PATH);
  const serverTag = server ? kidScriptTag(server) : undefined;

  // 2/3. Transform the page: inline asset refs FIRST (script/style src attrs
  // are text files, never assets, so they survive untouched), then swap the
  // reference tags for inline content.
  let html = inlineAssetRefs(pageFile.content, assets);
  html = html.replace(SCRIPT_REF_RE, (_m, dq: string | undefined, sq: string | undefined) => {
    const src = normalizeRef(dq ?? sq ?? '');
    // The page's own server.js reference is dropped — it already runs first.
    if (src === SERVER_PATH) return '<!-- server.js runs first (injected by the studio) -->';
    const file = files.find((f) => f.kind === 'text' && f.path === src);
    return file ? kidScriptTag(file) : missingRefTag(src);
  });
  html = html.replace(LINK_TAG_RE, (tag: string) => {
    if (!/\brel\s*=\s*["']stylesheet["']/i.test(tag)) return tag; // icons etc. — leave
    const href = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(tag);
    const ref = normalizeRef(href?.[1] ?? href?.[2] ?? '');
    const file = files.find((f) => f.kind === 'text' && f.path === ref);
    if (!file) return missingRefTag(ref);
    const css = inlineAssetRefs(file.content, assets);
    scanned.push({ text: css, kind: 'css' });
    return `<style>${escapeStyleText(css)}</style>`;
  });
  scanned.push({ text: html, kind: 'html' });
  // A media reference that matches NO project file is named NOW, at build
  // time: at run time the CSP only reports the blocked directive (content-free),
  // so this line is the only place the kid — and the AI's fix turn — can read
  // WHICH file name to correct.
  const scanOrder = { html: 0, css: 1, js: 2 } as const;
  scanned.sort((a, b) => scanOrder[a.kind] - scanOrder[b.kind]);
  const missingMediaTags = findUnresolvedMediaRefs(scanned, assets).map(
    (ref) => `<script>console.error(${jsStr(missingMediaMessage(ref))});${'<'}/script>`,
  );

  // NOTE (D-WEB-15): `data/*.json` files never enter the frame — they are the
  // SEEDS the backend rebuilds the server-side db from (`POST …/db/reset`).
  // The shim's `db.query` reaches that db over the sql postMessage channel.

  // ── STUDIO-OWNED SKELETON (security-load-bearing — never locate the
  // injection point by matching markup in the UNTRUSTED page). The kid page is
  // parsed with DOMParser (no execution, malformed markup repaired) and its
  // head children + body are lifted in AFTER the shims — so a `<head>` hidden
  // in a comment / string literal / attribute can never displace the fetch
  // shim or the CSP (the fences the backend's website fetch( allowance rests
  // on). A DOMParser failure degrades to raw page content in the body — still
  // strictly AFTER every shim byte.
  let kidHead = '';
  let kidBody = html;
  let kidBodyAttrs = '';
  // `<html>` attributes are carried too: every backend template ships
  // `<html lang="en">`, and an AI-written `<html class="dark">` pairs with
  // `html.dark {…}` CSS — dropping them renders the preview differently from
  // what the kid reads in the editor.
  let kidHtmlAttrs = '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    kidHead = doc.head.innerHTML;
    kidBody = doc.body.innerHTML;
    kidBodyAttrs = serializeAttrs(doc.body);
    kidHtmlAttrs = serializeAttrs(doc.documentElement);
  } catch {
    // keep the raw-content fallback above
  }

  const srcDoc = [
    '<!doctype html>',
    `<html${kidHtmlAttrs}><head><meta charset="utf-8">`,
    EXTENSION_NOISE_GUARD,
    SANDBOX_STORAGE_SHIM,
    CONSOLE_CAPTURE,
    // The asset-origin shim BEFORE the site runtime: a project path that first
    // exists at run time (a db row, an /api/ response, a computed string)
    // resolves to the inlined bytes wherever the page uses it.
    buildAssetOriginShim(assets),
    siteRuntime(pageFile.path),
    // Chatbot Studio only: the `window.ai` bridge rides right after the site
    // runtime, still BEFORE the CSP and every kid byte (D-CB-06).
    ...(opts.chat ? [siteChatRuntime(opts.chat)] : []),
    WEBSITE_RUNTIME_KIT,
    CSP_META,
    WEBSITE_FOUNDATION,
    // server.js — the FIRST kid script, before anything the page brought along
    // (even an inline script the kid page put in its own head).
    ...(serverTag ? [serverTag] : []),
    ...missingMediaTags,
    kidHead,
    '</head>',
    `<body${kidBodyAttrs}>`,
    kidBody,
    '</body></html>',
  ].join('\n');

  // 4. Locate every inlined kid script in the final document → line ranges.
  // The tag text is unique per (path, content); content starts ON the
  // `<script>` line, so kid-file line N = tag line + (N - 1).
  const lineOf = (offset: number): number => {
    let line = 1;
    for (let i = 0; i < offset; i += 1) if (srcDoc.charCodeAt(i) === 10) line += 1;
    return line;
  };
  const scriptRanges: ScriptLineRange[] = [];
  for (const [tag, { path, content }] of injected) {
    let from = 0;
    for (;;) {
      const at = srcDoc.indexOf(tag, from);
      if (at === -1) break;
      const start = lineOf(at);
      scriptRanges.push({ path, start, end: start + content.split('\n').length - 1 });
      from = at + tag.length;
    }
  }
  scriptRanges.sort((a, b) => a.start - b.start);

  return { srcDoc, scriptRanges, currentPage: pageFile.path };
}
